<template>
  <div>
    <v-autocomplete
      ref="autocomplete"
      :value="selected"
      :items="sortedItems"
      :multiple="multiple"
      :label="label"
      :loading="loading"
      :rules="rules"
      item-value="id"
      :search-input.sync="searchOptions.q"
      :hide-selected="hideSelected"
      dense
      no-filter
      return-object
      @change="changedValue"
      @blur="resetPages"
    >
      <template v-slot:selection="{ item, index }">
        <template v-if="hideSelected">
          <template v-if="index == 0">
            {{ label }}
          </template>
        </template>
        <template v-else-if="multiple">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-chip
                v-if="index < maxSelectedToRender"
                :close="deletableChips"
                v-on="on"
                @click:close="removeSelection(index)"
              >
                <span>{{ $_.truncate(item[selectedItemText], {length: 25}) }}</span>
              </v-chip>
            </template>
            <span>{{ item[selectedItemText] }}</span>
          </v-tooltip>
          <span
            v-if="index === maxSelectedToRender"
          >+{{ (sanitizedSelected.length || 0) - (maxSelectedToRender) }}</span>
        </template>
        <template v-else>
          <span>{{ item[selectedItemText] }}</span>
        </template>
      </template>
      <template v-slot:item="{item}">
        <slot
          name="item"
          :item="item"
        >
          {{ item[itemText] }}
        </slot>
      </template>
      <div
        v-if="!loading"
        slot="append-item"
        v-intersect.quiet="onIntersect"
      />
      <v-progress-linear
        slot="append-item"
        :active="loading"
        :indeterminate="true"
        :value="0"
        height="2"
      />
    </v-autocomplete>
    <v-row
      v-if="$slots['summary']"
      class="justify-end mx-0"
    >
      <slot
        name="summary"
      />
    </v-row>
  </div>
</template>

<script>
  import axios from 'axios'
  import _ from 'lodash'
  export default {
    props: {
      endpoint: {
        type: String,
        default: '',
      },
      params: {
        type: Object,
        default: () => { return {} },
      },
      itemText: {
        type: String,
        default: 'name',
      },
      selectedItemText: {
        type: String,
        default: 'name',
      },
      label: {
        type: String,
        default: '',
      },
      useDev: {
        type: Boolean,
        default: () => false,
      },
      multiple: {
        type: Boolean,
        default: () => false,
      },
      ignoreSelected: {
        type: Boolean,
        default: () => true,
      },
      deletableChips: {
        type: Boolean,
        default: () => true,
      },
      selected: {
        type: [Array, Object],
        default: () => [],
      },
      searchDelay: {
        type: Number,
        default: 200,
      },
      maxSelectedToRender: {
        type: Number,
        default: 5,
      },
      rules: {
        type: Array,
        default: () => [],
      },
      hideSelected: {
        type: Boolean,
        default: false,
      },
      extraParams: {
        type: Object,
        default: () => ({}),
      },
    },
    data () {
      return {
        items: [],
        totalItems: 0,
        options: {
          limit: null,
          page: 1,
          q: '',
        },
        searchOptions: {
          limit: null,
          page: 1,
          q: '',
        },
        loading: false,
        lastPage: null,
        cancelToken: null,
        debounceId: null,
        existingProductsIds: [],
      }
    },
    computed: {
      sanitizedSelected: function () {
        if (this.$_.isEmpty(this.selected)) { return [] }
        if (this.multiple) { return this.$_.isArray(this.selected) ? [...this.selected] : [] }

        return [this.selected]
      },
      computedParams: function () {
        return {
          ...this.params,
          ...this.extraParams,
          ...this.options,
        }
      },
      computedSearchParams: function () {
        return {
          ...this.params,
          ...this.extraParams,
          ...this.searchOptions,
        }
      },
      selectedIds: function () {
        return this.ignoreSelected ? [] : this.$_.map(this.sanitizedSelected, 'id')
      },
      sortedItems: function () {
        const items = [...this.items]
        return this.$_.sortBy(items, i => {
          return !this.$_.find(this.sanitizedSelected, ['id', i.id])
        })
      },
    },
    watch: {
      computedParams: {
        handler (val, oldVal) {
          if (this.$_.isEqual(val, oldVal) && val.limit !== null) { return }
          if (val.limit && !oldVal.limit) { return }
          if (val.page === 1) { this.lastPage = null; this.existingProductsIds = []; this.searchOptions.page = 1 }

          this.getDataFromApi(true, val)
        },
        deep: true,
      },
      computedSearchParams: {
        handler (val, oldVal) {
          if (this.$_.isEqual(val, oldVal)) { return }
          if (val.page === 1) { this.lastPage = null; this.existingProductsIds = []; this.options.page = 1 }
          clearTimeout(this.debounceId)
          if (val.page === oldVal.page) { this.debounceId = setTimeout(() => { if (val.q) this.getDataFromApi(false, val, true) }, this.searchDelay) } else this.debounceId = setTimeout(() => { if (val.q) this.getDataFromApi(true, val, true) }, this.searchDelay)
        },
        deep: true,
      },
    },
    created () {
      this.getDataFromApi(true, this.computedParams)
    },
    methods: {
      getDataFromApi: function (append = true, params, force) {
        if (this.loading && !force) { return }

        if (this.cancelToken) { this.cancelToken.cancel('cancelled') }
        this.cancelToken = axios.CancelToken.source()
        this.loading = true
        this.$get(`/${this.endpoint}`, { ...params, selected: this.selectedIds.join(','), cancelToken: this.cancelToken.token }, { useDev: this.useDev })
          .then(response => {
            // merge arrays so items is not duplicated based on id
            const rdd = [...response.data.data, ...this.sanitizedSelected]
            this.items = [...rdd, ...this.items]
            this.items = this.$_.uniqBy(this.items, function (e) {
              return e.id
            })
            this.existingProductsIds = [...this.$_.map(this.items, 'id')]

            this.lastPage = response.data.last_page
            if (!this.options.limit) {
              this.options.limit = response.data.per_page
              this.searchOptions.limit = response.data.per_page
            }
            this.loading = false
          })
          .catch(e => {
            if (e !== 'cancelled') this.loading = false
          })
      },
      onIntersect: function (entries, observer, isIntersecting) {
        const o = this.searchOptions.q ? this.searchOptions : this.options

        if (isIntersecting && o.page && o.page !== this.lastPage) {
          if (this.searchOptions.q) { this.searchOptions.page = this.searchOptions.page + 1 } else { this.options.page = this.options.page + 1 }
        }
      },
      changedValue: function (val) {
        this.$emit('value-change', val)
      },
      removeSelection: function (index) {
        const selected = [...this.sanitizedSelected]
        selected.splice(index, 1)
        this.$emit('value-change', selected)
      },
      resetPages: function () {
        this.options.page = 1
        this.searchOptions.page = 1
        this.lastPage = null
        this.$emit('blur')
      },
    },

  }
</script>
